import {
  type Dispatch,
  type MutableRefObject,
  type RefObject,
  type SetStateAction,
  useEffect,
} from 'react'
import type { CropOverlayRef } from '@/components/app/editor/tools/crop-overlay'
import type { CropToolbarRef } from '@/components/app/editor/tools/crop-toolbar'
import type { CropZoomSliderRef } from '@/components/app/editor/tools/crop-zoom-slider'
import type { NodeEditToolbarRef } from '@/components/app/editor/tools/node-edit-toolbar'
import type { TextOnPathToolbarRef } from '@/components/app/editor/tools/text-on-path-toolbar'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'

/**
 * Escape/Enter (and Tab, for crop) for the three single-shape edit modes — node edit, Text on a
 * Path, crop — handled in the window capture phase, ahead of Radix's document capture listener.
 * An in-progress drag in the mode's own UI is cancelled before the mode is exited. The three
 * listeners are registered in this order, which decides which runs first.
 */
export function useEditModeKeyCapture({
  internalShapeManagerRef,
  isEditableTarget,
  isNodeEditMode,
  nodeEditShapeId,
  nodeEditToolbarRef,
  exitNodeEditMode,
  isTextOnPathMode,
  textOnPathToolbarRef,
  commitAndExitTextOnPathMode,
  isCropMode,
  cropShapeId,
  setCropShapeData,
  cropOverlayRef,
  cropZoomSliderRef,
  cropToolbarRef,
  cropGestureBeforeRef,
  exitCropMode,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  isEditableTarget: (target: EventTarget | null) => boolean
  isNodeEditMode: boolean
  nodeEditShapeId: string | null
  nodeEditToolbarRef: RefObject<NodeEditToolbarRef | null>
  exitNodeEditMode: (exitingShapeId: string | undefined) => void
  isTextOnPathMode: boolean
  textOnPathToolbarRef: RefObject<TextOnPathToolbarRef | null>
  commitAndExitTextOnPathMode: () => void
  isCropMode: boolean
  cropShapeId: string | null
  setCropShapeData: Dispatch<SetStateAction<ShapeData | null>>
  cropOverlayRef: RefObject<CropOverlayRef | null>
  cropZoomSliderRef: RefObject<CropZoomSliderRef | null>
  cropToolbarRef: RefObject<CropToolbarRef | null>
  cropGestureBeforeRef: MutableRefObject<ShapeData | null>
  exitCropMode: () => void
}) {
  // Node edit mode: handle Escape/Enter in window capture phase so they fire before
  // Radix UI's document capture listener (used by Tooltip/DismissableLayer). Without
  // this, a visible tooltip consumes the first Escape and the user must press twice.
  useEffect(() => {
    if (!isNodeEditMode) return

    const handleKeyCapture = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' && e.key !== 'Enter') return

      // An in-progress toolbar grip-handle drag takes priority over exiting node-edit mode —
      // cancel it and stop it in place instead, mirroring crop mode's own
      // in-progress-drag-takes-priority handling. Only applies to Escape (Enter has no
      // "cancel a drag" meaning).
      if (e.key === 'Escape' && nodeEditToolbarRef.current?.cancelActiveDrag()) {
        e.stopPropagation()
        return
      }

      if (isEditableTarget(e.target)) return
      e.stopPropagation()
      const editId = nodeEditShapeId

      exitNodeEditMode(editId ?? undefined)
      if (editId) {
        internalShapeManagerRef.current?.selectShapes([editId])
      }
    }

    window.addEventListener('keydown', handleKeyCapture, true)
    return () => window.removeEventListener('keydown', handleKeyCapture, true)
  }, [
    isNodeEditMode,
    nodeEditShapeId,
    exitNodeEditMode,
    isEditableTarget,
    nodeEditToolbarRef,
    internalShapeManagerRef,
  ])

  // Text on a Path mode: same capture-phase rationale as the node-edit and crop handlers here —
  // Radix's document capture listener would otherwise let a visible tooltip or the Position
  // popover eat the first Escape.
  useEffect(() => {
    if (!isTextOnPathMode) return

    const handleKeyCapture = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return

      // Order is load-bearing. `isEditableTarget` matches this bar's <textarea> AND the Radix
      // slider thumb's role="slider" — and that thumb is the event target mid-drag — so asking
      // it first would swallow every drag cancel. Cancel the drag, then bail on editable
      // targets, then exit.
      if (textOnPathToolbarRef.current?.cancelActiveDrag()) {
        e.stopPropagation()
        return
      }

      if (isEditableTarget(e.target)) {
        // The textarea IS an editable target, and it holds focus for the whole session — so
        // unlike node-edit/crop, bailing here outright would make Escape never close the bar.
        // Only defer to genuinely other editable surfaces.
        const target = e.target as HTMLElement | null

        if (target?.closest('[data-text-on-path-toolbar]') == null) return
      }

      e.stopPropagation()
      // Escape COMMITS, it does not discard. The design ticket embedded in the Figma spec is
      // explicit — "clicking outside the shape/toolbar (or the sidebar's Done button, or Esc)
      // commits" — and a discard here silently destroyed whatever had just been typed. AC-15 and
      // AC-16 describe the opposite and need amending on WMPCM-9542; there is now no gesture that
      // throws the text away, which is the safer of the two failure modes.
      commitAndExitTextOnPathMode()
    }

    window.addEventListener('keydown', handleKeyCapture, true)
    return () => window.removeEventListener('keydown', handleKeyCapture, true)
  }, [isTextOnPathMode, isEditableTarget, commitAndExitTextOnPathMode, textOnPathToolbarRef])

  // Crop mode: handle Escape/Enter/Tab in window capture phase, same rationale as node-edit above.
  useEffect(() => {
    if (!isCropMode) return

    const handleKeyCapture = (e: KeyboardEvent) => {
      // Tab completes crop mode exactly like Enter (see the exit branch below) — added
      // alongside Escape/Enter rather than as a separate handler so it shares every existing
      // guard (in-progress-drag priority, isEditableTarget) with Enter exactly.
      if (e.key !== 'Escape' && e.key !== 'Enter' && e.key !== 'Tab') return

      // An in-progress drag — resize, pan (photo move), photo-rotate, shape-rotate, or the
      // zoom-slider — takes priority over exiting crop mode — cancel and revert it instead,
      // mirroring the normal select-tool's own Escape-cancels-resize behavior. Only applies to
      // Escape (Enter/Tab have no "cancel a drag" meaning). Short-circuits after the first
      // match, since only one of these gestures can be mid-drag at a time.
      //
      // Deliberately checked BEFORE `isEditableTarget` below: the zoom slider's Radix thumb
      // carries `role="slider"`, which `isEditableTarget` matches (it's meant to keep Escape
      // from leaking into an unrelated slider/textbox elsewhere in the UI) — but that thumb is
      // exactly the element holding focus/being `e.target` while THIS drag is in progress, so
      // checking `isEditableTarget` first would always block the cancel. The cancel functions
      // are self-guarding no-ops when nothing is actually dragging, so calling them regardless
      // of focus target is safe.
      if (
        e.key === 'Escape' &&
        (cropOverlayRef.current?.cancelActiveDrag() ||
          cropZoomSliderRef.current?.cancelActiveDrag() ||
          cropToolbarRef.current?.cancelActiveDrag())
      ) {
        e.stopPropagation()

        const before = cropGestureBeforeRef.current

        cropGestureBeforeRef.current = null

        if (before) {
          internalShapeManagerRef.current?.updateShape(before.id, before)
          setCropShapeData(before)
        }

        return
      }

      if (isEditableTarget(e.target)) return

      // Tab's own default action (move focus to the next tabbable element, e.g. a toolbar
      // button) would otherwise fight with completing crop mode — suppress it so Tab only
      // ever exits crop mode while cropping, matching Enter's behavior exactly.
      if (e.key === 'Tab') {
        e.preventDefault()
      }

      e.stopPropagation()
      const editId = cropShapeId

      exitCropMode()
      if (editId) {
        internalShapeManagerRef.current?.selectShapes([editId])
      }
    }

    window.addEventListener('keydown', handleKeyCapture, true)
    return () => window.removeEventListener('keydown', handleKeyCapture, true)
  }, [
    isCropMode,
    cropShapeId,
    isEditableTarget,
    cropOverlayRef,
    cropZoomSliderRef,
    cropToolbarRef,
    cropGestureBeforeRef,
    internalShapeManagerRef,
    setCropShapeData,
    exitCropMode,
  ])
}
